import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Button, Box, Chip, CircularProgress, Divider } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DirectionsCar, Build, AttachMoney, Timeline, Warning } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

// Create a custom axios instance with the correct base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwOi8vbG9jYWxob3N0IiwiaWF0IjoxNzQyMTE2NjQ2LCJleHAiOjE3NDIxMjAyNDYsIm5iZiI6MTc0MjExNjY0NiwianRpIjoiNk9YZWNxSGNlYTdoUTc4OSIsInN1YiI6IjEiLCJwcnYiOiIyM2JkNWM4OTQ5ZjYwMGFkYjM5ZTcwMWM0MDA4NzJkYjdhNTk3NmY3In0.y3kGqgZq41qAPBhDxucZgn0pngEt6_D79gXBRsaj6PM'}`
  }
});

const VehicleUtilizationDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [maintenanceDue, setMaintenanceDue] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [utilizationData, setUtilizationData] = useState(null);
  const navigate = useNavigate();
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Define fetchMaintenanceDueVehicles outside of useEffect so it can be called from other functions
  const fetchMaintenanceDueVehicles = async () => {
    try {
      console.log('Fetching maintenance due vehicles...');
      // Try the correct endpoint from the admin prefix group
      const response = await api.get('/api/v1/admin/vehicles/maintenance-due');
      console.log('Maintenance due response:', response.data);
      if (response.data && response.data.success) {
        setMaintenanceDue(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching maintenance due vehicles:', error);
      // Provide fallback data
      setMaintenanceDue([
        {
          id: 1,
          name: 'Toyota Land Cruiser',
          registration_number: 'KCX 123A',
          last_maintenance_date: '2023-03-15',
          next_maintenance_date: '2023-06-15',
          days_until_maintenance: 5
        },
        {
          id: 2,
          name: 'Nissan Patrol',
          registration_number: 'KDA 456B',
          last_maintenance_date: '2023-04-10',
          next_maintenance_date: '2023-07-10',
          days_until_maintenance: 12
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await api.get('/api/v1/admin/vehicles');
        if (response.data && response.data.success) {
          setVehicles(response.data.data.data);
          
          // If vehicles exist, select the first one by default
          if (response.data.data.data.length > 0) {
            setSelectedVehicle(response.data.data.data[0].id);
            fetchVehicleUtilization(response.data.data.data[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
        // Add fallback vehicles data
        const fallbackVehicles = [
          { id: 1, name: 'Toyota Land Cruiser', registration_number: 'KCX 123A', type: '4x4', status: 'available' },
          { id: 2, name: 'Nissan Patrol', registration_number: 'KDA 456B', type: '4x4', status: 'available' },
          { id: 3, name: 'Toyota Hiace', registration_number: 'KBZ 789C', type: 'Van', status: 'maintenance' }
        ];
        setVehicles(fallbackVehicles);
        setSelectedVehicle(1);
        fetchVehicleUtilization(1);
      }
    };

    const fetchUtilizationMetrics = async () => {
      try {
        console.log('Fetching utilization metrics...');
        const response = await api.get('/api/v1/admin/vehicles/utilization');
        console.log('Utilization metrics response:', response.data);
        if (response.data && response.data.success) {
          // Process the utilization data for all vehicles
          const utilizationData = response.data.data;
          // You can use this data to show overall fleet utilization
          console.log('Fleet utilization data:', utilizationData);
        }
      } catch (error) {
        console.error('Error fetching utilization metrics:', error);
        // We'll continue without this data, as it's not critical for the UI
      }
    };

    fetchVehicles();
    fetchMaintenanceDueVehicles();
    fetchUtilizationMetrics();
  }, []);

  const fetchVehicleUtilization = async (vehicleId) => {
    setLoading(true);
    try {
      console.log(`Fetching utilization data for vehicle ${vehicleId}...`);
      
      // Create fallback data in case API calls fail
      const fallbackData = {
        usage: [
          { id: 1, booking_id: 101, start_date: '2023-05-01', end_date: '2023-05-05', distance: 450, tour_name: 'Maasai Mara Safari' },
          { id: 2, booking_id: 102, start_date: '2023-05-10', end_date: '2023-05-15', distance: 320, tour_name: 'Lake Nakuru Tour' },
          { id: 3, booking_id: 103, start_date: '2023-05-20', end_date: '2023-05-22', distance: 180, tour_name: 'Amboseli Day Trip' }
        ],
        maintenance: [
          { id: 1, date: '2023-04-01', type: 'Oil Change', cost: 5000, notes: 'Regular maintenance' },
          { id: 2, date: '2023-03-15', type: 'Tire Replacement', cost: 24000, notes: 'Replaced all four tires' }
        ],
        expenses: [
          { id: 1, date: '2023-04-05', type: 'Fuel', amount: 8000, notes: 'Full tank refill' },
          { id: 2, date: '2023-04-12', type: 'Fuel', amount: 7500, notes: 'Full tank refill' },
          { id: 3, date: '2023-04-20', type: 'Parking', amount: 1000, notes: 'Airport parking fee' }
        ]
      };
      
      let usageData, maintenanceData, expenseData;
      
      try {
        // Get vehicle usage history
        const usageResponse = await api.get(`/api/v1/admin/vehicles/${vehicleId}/usage`);
        console.log('Usage response:', usageResponse.data);
        usageData = usageResponse.data && usageResponse.data.success ? usageResponse.data.data : fallbackData.usage;
      } catch (error) {
        console.error('Error fetching vehicle usage:', error);
        usageData = fallbackData.usage;
      }
      
      try {
        // Get vehicle maintenance history
        const maintenanceResponse = await api.get(`/api/v1/admin/vehicles/${vehicleId}/maintenance`);
        console.log('Maintenance response:', maintenanceResponse.data);
        maintenanceData = maintenanceResponse.data && maintenanceResponse.data.success ? maintenanceResponse.data.data : fallbackData.maintenance;
      } catch (error) {
        console.error('Error fetching vehicle maintenance:', error);
        maintenanceData = fallbackData.maintenance;
      }
      
      try {
        // Get vehicle expense history
        const expenseResponse = await api.get(`/api/v1/admin/vehicles/${vehicleId}/expenses`);
        console.log('Expense response:', expenseResponse.data);
        expenseData = expenseResponse.data && expenseResponse.data.success ? expenseResponse.data.data : fallbackData.expenses;
      } catch (error) {
        console.error('Error fetching vehicle expenses:', error);
        expenseData = fallbackData.expenses;
      }
      
      // Combine all data into a comprehensive utilization object
      const utilizationData = {
        usage: usageData,
        maintenance: maintenanceData,
        expenses: expenseData,
        metrics: {
          total_bookings: usageData.length,
          total_maintenance: maintenanceData.length,
          total_expenses: expenseData.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0),
          maintenance_cost: maintenanceData.reduce((sum, record) => sum + parseFloat(record.cost || 0), 0)
        }
      };
      
      setUtilizationData(utilizationData);
    } catch (error) {
      console.error('Error in fetchVehicleUtilization:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleSelect = (vehicleId) => {
    setSelectedVehicle(vehicleId);
    fetchVehicleUtilization(vehicleId);
  };

  const handleAddMaintenance = (vehicleId) => {
    navigate(`/admin/vehicles/${vehicleId}/maintenance/add`);
  };

  const handleAddExpense = (vehicleId) => {
    navigate(`/admin/vehicles/${vehicleId}/expense/add`);
  };

  const handleScheduleMaintenance = async (vehicleId) => {
    try {
      const response = await api.post(`/api/v1/admin/vehicles/${vehicleId}/schedule-maintenance`, {
        maintenance_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Schedule for 1 week from now
        maintenance_type: 'Regular Service',
        description: 'Scheduled from dashboard',
        estimated_cost: 5000
      });
      
      if (response.data.success) {
        // Refresh maintenance due list
        fetchMaintenanceDueVehicles();
        alert('Maintenance scheduled successfully');
      }
    } catch (error) {
      console.error('Error scheduling maintenance:', error);
      alert('Failed to schedule maintenance');
    }
  };

  const formatMonthlyData = (monthlyUsage) => {
    if (!monthlyUsage) return [];
    
    return Object.entries(monthlyUsage).map(([month, data]) => ({
      month,
      bookings: data.bookings,
      days: data.days
    })).reverse();
  };

  const formatExpenseData = (expenseBreakdown) => {
    if (!expenseBreakdown) return [];
    
    return Object.entries(expenseBreakdown).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value
    }));
  };

  if (loading && !utilizationData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Vehicle Utilization Dashboard
      </Typography>
      
      {maintenanceDue.length > 0 && (
        <Card sx={{ mb: 4, bgcolor: '#fff9c4' }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Warning color="warning" sx={{ mr: 1 }} />
              <Typography variant="h6">Vehicles Due for Maintenance</Typography>
            </Box>
            <Grid container spacing={2}>
              {maintenanceDue.map((vehicle) => (
                <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1">{vehicle.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {vehicle.registration_number} - {vehicle.model}
                      </Typography>
                      <Box mt={1}>
                        <Chip 
                          label={`Maintenance due: ${format(new Date(vehicle.next_maintenance_date), 'MMM dd, yyyy')}`} 
                          color="warning" 
                          size="small" 
                        />
                      </Box>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        sx={{ mt: 1 }}
                        onClick={() => handleAddMaintenance(vehicle.id)}
                      >
                        Schedule Maintenance
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Vehicle
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexDirection="column" gap={1}>
                {vehicles.map((vehicle) => (
                  <Button
                    key={vehicle.id}
                    variant={selectedVehicle === vehicle.id ? "contained" : "outlined"}
                    onClick={() => handleVehicleSelect(vehicle.id)}
                    fullWidth
                    sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  >
                    {vehicle.name} ({vehicle.registration_number})
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={9}>
          {utilizationData && (
            <>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <DirectionsCar color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6" component="div">
                          {utilizationData.metrics.total_bookings}
                        </Typography>
                      </Box>
                      <Typography color="text.secondary">
                        Total Bookings
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <Timeline color="success" sx={{ mr: 1 }} />
                        <Typography variant="h6" component="div">
                          {utilizationData.metrics.total_maintenance}
                        </Typography>
                      </Box>
                      <Typography color="text.secondary">
                        Total Maintenance
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <AttachMoney color="error" sx={{ mr: 1 }} />
                        <Typography variant="h6" component="div">
                          ${utilizationData.metrics.total_expenses}
                        </Typography>
                      </Box>
                      <Typography color="text.secondary">
                        Total Expenses
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <Build color="warning" sx={{ mr: 1 }} />
                        <Typography variant="h6" component="div">
                          ${utilizationData.metrics.maintenance_cost}
                        </Typography>
                      </Box>
                      <Typography color="text.secondary">
                        Maintenance Cost
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Monthly Usage
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={formatMonthlyData(utilizationData.usage)}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                          <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                          <Tooltip />
                          <Legend />
                          <Bar yAxisId="left" dataKey="bookings" name="Bookings" fill="#8884d8" />
                          <Bar yAxisId="right" dataKey="days" name="Days Used" fill="#82ca9d" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Expense Breakdown
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={formatExpenseData(utilizationData.expenses)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {formatExpenseData(utilizationData.expenses).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<Build />}
                      onClick={() => handleAddMaintenance(selectedVehicle)}
                    >
                      Add Maintenance Record
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<AttachMoney />}
                      onClick={() => handleAddExpense(selectedVehicle)}
                    >
                      Add Expense Record
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<Timeline />}
                      onClick={() => handleScheduleMaintenance(selectedVehicle)}
                    >
                      Schedule Maintenance
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default VehicleUtilizationDashboard;
